import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { playTestSound, stopAllAudio } from '../utils/audioUtils';
import { saveToStorage, loadFromStorage, STORAGE_KEYS } from '../utils/localStorage';
import { useFullscreen } from '../contexts/FullscreenContext';

export function useVideoAlarm(currentTime) {
  const { isFullscreen } = useFullscreen();
  // 加载视频闹钟设置
  const loadSettings = () => {
    const defaults = {
      alarmHour: '07',
      alarmMinute: '00',
      snoozeMinutes: '05',
      selectedMelody: 'CLASSIC',
      growing: false
    };
    
    return loadFromStorage(STORAGE_KEYS.VIDEO_ALARM_SETTINGS, defaults);
  };

  // 加载视频特定设置
  const loadVideoSettings = () => {
    const defaults = {
      youtubeUrl: '',
      videoId: '',
      volume: 70
    };
    
    return loadFromStorage('video_alarm_settings', defaults);
  };

  // 加载视频闹钟激活状态
  const loadActiveState = () => {
    const defaults = { isActive: false, activatedAt: null };
    return loadFromStorage(STORAGE_KEYS.VIDEO_ALARM_ACTIVE_STATE, defaults);
  };

  const savedSettings = loadSettings();
  const savedVideoSettings = loadVideoSettings();
  const savedActiveState = loadActiveState();

  const [alarmHour, setAlarmHour] = useState(savedSettings.alarmHour);
  const [alarmMinute, setAlarmMinute] = useState(savedSettings.alarmMinute);
  const [snoozeMinutes, setSnoozeMinutes] = useState(savedSettings.snoozeMinutes);
  const [selectedMelody, setSelectedMelody] = useState(savedSettings.selectedMelody);
  const [growing, setGrowing] = useState(savedSettings.growing);
  const [alarmActive, setAlarmActive] = useState(savedActiveState.isActive);
  const [alarmRinging, setAlarmRinging] = useState(false);
  const [timeLeft, setTimeLeft] = useState('');

  // 视频相关状态
  const [youtubeUrl, setYoutubeUrl] = useState(savedVideoSettings.youtubeUrl);
  const [videoId, setVideoId] = useState(savedVideoSettings.videoId);
  const [volume, setVolume] = useState(savedVideoSettings.volume);
  const [ytApiReady, setYtApiReady] = useState(false);
  const [showPlayPrompt, setShowPlayPrompt] = useState(false);
  const [audioUnlocked, setAudioUnlocked] = useState(false);

  const audioControlRef = useRef(null);
  const playerRef = useRef(null);
  const audioContextRef = useRef(null);
  const playerContainerIdRef = useRef('youtube-player');
  const playerMountIdRef = useRef(null);
  const timeSettingsRef = useRef({ alarmHour: savedSettings.alarmHour, alarmMinute: savedSettings.alarmMinute });
  const isSnoozingRef = useRef(false);
  const alarmRingingRef = useRef(false);

  // 保存激活状态到 localStorage
  const saveActiveState = useCallback((isActive) => {
    const activeState = {
      isActive,
      activatedAt: isActive ? Date.now() : null
    };
    
    saveToStorage(STORAGE_KEYS.VIDEO_ALARM_ACTIVE_STATE, activeState);
  }, []);

  // 自定义的 setAlarmActive 函数，同时保存状态
  const setAlarmActiveWithSave = useCallback((isActive) => {
    setAlarmActive(isActive);
    saveActiveState(isActive);
  }, [saveActiveState]);

  // 热门视频列表
  const popularVideos = useMemo(() => [
    { id: 'dQw4w9WgXcQ', title: 'Energetic Music - Never Gonna Give You Up' },
    { id: 'ZbZSe6N_BXs', title: 'Happy - Pharrell Williams' },
    { id: 'CevxZvSJLk8', title: 'Katy Perry - Roar' },
    { id: 'nfs8NYg7yQM', title: 'Morning Motivation Music' },
    { id: 'y6Sxv-sUYtM', title: 'Peaceful Morning Music' }
  ], []);

  // 解锁音频上下文
  const unlockAudio = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    
    // 播放静音音频来解锁
    const oscillator = audioContextRef.current.createOscillator();
    const gainNode = audioContextRef.current.createGain();
    gainNode.gain.value = 0; // 静音
    oscillator.connect(gainNode);
    gainNode.connect(audioContextRef.current.destination);
    oscillator.start();
    oscillator.stop(audioContextRef.current.currentTime + 0.01);
    
    setAudioUnlocked(true);
    console.log('Audio context unlocked');
  }, []);

  // 播放警告声音
  const playAlertSound = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    
    const ctx = audioContextRef.current;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
    
    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.5);
    
    console.log('Alert sound played');
  }, []);

  // 创建 YouTube 播放器
  const createYouTubePlayer = useCallback((container, options = {}) => {
    const { allowFullscreen = false, autoplay = undefined } = options;
    
    if (!videoId) {
      console.warn('createYouTubePlayer called without videoId');
      return;
    }

    let targetElement = null;
    let containerId = null;

    if (typeof container === 'string') {
      containerId = container;
      targetElement = document.getElementById(container);
    } else if (container && typeof container === 'object' && 'nodeType' in container) {
      targetElement = container;
      containerId = container.id;
      if (!containerId) {
        containerId = `youtube-player-${Date.now()}`;
        container.id = containerId;
      }
    } else {
      containerId = playerContainerIdRef.current || 'youtube-player';
      targetElement = document.getElementById(containerId);
    }

    // 检查元素是否存在且可见
    const isElementVisible = (element) => {
      if (!element) return false;
      
      // 检查元素本身是否可见
      if (element.offsetParent === null) return false;
      
      // 检查父容器是否有hidden类
      const parentElement = element.parentElement;
      if (parentElement && parentElement.classList.contains('hidden')) return false;
      
      return true;
    };

    if (!targetElement || !isElementVisible(targetElement)) {
      console.warn('YouTube player element not found or not visible, retrying...');
      console.log('Debug info:', {
        containerId,
        videoId,
        elementExists: !!targetElement,
        elementVisible: targetElement ? isElementVisible(targetElement) : false,
        parentClass: targetElement?.parentElement?.className,
        allYoutubeElements: document.querySelectorAll('[id*="youtube"]').length
      });
      
      // 延迟重试，等待DOM渲染完成和容器变为可见
      setTimeout(() => {
        const retryElement = document.getElementById(containerId);
        console.log('Retry attempt:', {
          containerId,
          elementExists: !!retryElement,
          elementVisible: isElementVisible(retryElement),
          parentClass: retryElement?.parentElement?.className
        });
        
        if (retryElement && isElementVisible(retryElement)) {
          console.log('YouTube player element found on retry and is visible');
          createYouTubePlayer(containerId, options);
        } else {
          console.error('YouTube player element still not found after retry or is not visible');
          setShowPlayPrompt(true);
        }
      }, 500);
      return;
    }

    playerContainerIdRef.current = containerId;

    // 防止 YouTube API 替换 React 管理的节点，改为在内部挂载
    let wrapperElement = targetElement;
    if (typeof targetElement === 'string') {
      wrapperElement = document.getElementById(targetElement);
    }

    if (!(wrapperElement instanceof HTMLElement)) {
      console.error('YouTube wrapper element invalid');
      setShowPlayPrompt(true);
      return;
    }

    if (!wrapperElement.id) {
      wrapperElement.id = containerId || `youtube-player-${Date.now()}`;
      containerId = wrapperElement.id;
      playerContainerIdRef.current = containerId;
    }

    if (isFullscreen && !allowFullscreen) {
      console.log('Fullscreen mode, skipping YouTube player creation');
      return;
    }

    if (!window.YT || !window.YT.Player) {
      console.error('YouTube API not available');
      setShowPlayPrompt(true);
      return;
    }

    if (playerRef.current) {
      try {
        console.log('Destroying previous YouTube player');
        if (typeof playerRef.current.destroy === 'function') {
          playerRef.current.destroy();
        }
      } catch (error) {
        console.error('Error destroying previous player:', error);
      } finally {
        playerRef.current = null;
      }
    }

    // 清空内部内容，创建专用挂载节点，让 React 只管理外层容器
    while (wrapperElement.firstChild) {
      try {
        wrapperElement.removeChild(wrapperElement.firstChild);
      } catch (error) {
        console.warn('Error removing child node during cleanup:', error);
        break; // 如果出错就停止循环
      }
    }

    const mountElement = document.createElement('div');
    const mountId = `${wrapperElement.id}-mount`;
    mountElement.id = mountId;
    mountElement.setAttribute('data-yt-player-mount', 'true');
    wrapperElement.appendChild(mountElement);
    playerMountIdRef.current = mountId;

    try {
      console.log('Creating YouTube player in container:', containerId);
      playerRef.current = new window.YT.Player(mountElement, {
        height: '450',
        width: '100%',
        videoId: videoId,
        playerVars: {
          autoplay:
            typeof autoplay === 'number'
              ? autoplay
              : (alarmRingingRef.current ? 1 : 0),
          controls: 1,
          enablejsapi: 1,
          origin: window.location.origin,
          start: 0,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          fs: 1,
          cc_load_policy: 0,
          iv_load_policy: 3,
          disablekb: 0
        },
        events: {
          onReady: (event) => {
            console.log('YouTube player ready');
            try {
              event.target.setVolume(volume);
              const shouldAttemptAutoplay =
                autoplay === 1 || alarmRingingRef.current;

              const tryPlay = async () => {
                try {
                  await event.target.mute();
                } catch (muteError) {
                  console.warn('Mute attempt failed:', muteError);
                }
                try {
                  await event.target.playVideo();
                } catch (playError) {
                  console.warn('Autoplay attempt rejected:', playError);
                }
              };

              if (shouldAttemptAutoplay) {
                tryPlay();
              }

              const ensurePlaying = () => {
                try {
                  const state = event.target.getPlayerState();
                  if (
                    state === window.YT.PlayerState.PLAYING ||
                    state === window.YT.PlayerState.BUFFERING
                  ) {
                    setShowPlayPrompt(false);
                    setTimeout(() => {
                      try {
                        event.target.unMute();
                        event.target.setVolume(volume);
                      } catch (unmuteError) {
                        console.warn('Failed to unmute automatically:', unmuteError);
                      }
                    }, 500);
                    return;
                  }
                } catch (stateError) {
                  console.warn('Error reading player state:', stateError);
                }

                console.log('Video not playing automatically, showing manual play prompt');
                setShowPlayPrompt(true);
              };

              setTimeout(ensurePlaying, 1500);
            } catch (error) {
              console.error('Error in onReady:', error);
              setShowPlayPrompt(true);
            }
          },
          onStateChange: (event) => {
            console.log('Player state changed:', event.data);
            
            if (event.data === window.YT.PlayerState.PLAYING) {
              setShowPlayPrompt(false);
            }
            
            if (event.data === window.YT.PlayerState.PAUSED && alarmRingingRef.current) {
              console.log('Video paused during alarm, attempting restart');
              setTimeout(() => {
                try {
                  event.target.playVideo();
                } catch (error) {
                  console.error('Error restarting video:', error);
                  setShowPlayPrompt(true);
                }
              }, 500);
            }
            
            if (event.data === window.YT.PlayerState.ENDED && alarmRingingRef.current) {
              console.log('Video ended during alarm, restarting');
              setTimeout(() => {
                try {
                  event.target.seekTo(0);
                  event.target.playVideo();
                } catch (error) {
                  console.error('Error restarting ended video:', error);
                  setShowPlayPrompt(true);
                }
              }, 100);
            }
          },
          onError: (event) => {
            console.error('YouTube player error:', event.data);
            setShowPlayPrompt(true);
          }
        }
      });
    } catch (error) {
      console.error('Error creating YouTube player:', error);
      setShowPlayPrompt(true);
    }
  }, [videoId, volume, isFullscreen]);

  // 手动开始播放
  const manualStartPlay = useCallback((container) => {
    console.log('Manual play requested');
    setShowPlayPrompt(false);
    
    let nextContainer = container;
    if (typeof container === 'string') {
      playerContainerIdRef.current = container;
      nextContainer = container;
    } else if (container && typeof container === 'object' && 'nodeType' in container) {
      const id = container.id || playerContainerIdRef.current || `youtube-player-${Date.now()}`;
      if (!container.id) container.id = id;
      playerContainerIdRef.current = id;
      nextContainer = container;
    } else if (playerContainerIdRef.current) {
      nextContainer = playerContainerIdRef.current;
    } else {
      nextContainer = 'youtube-player';
      playerContainerIdRef.current = nextContainer;
    }
    
    if (playerRef.current && typeof playerRef.current.playVideo === 'function') {
      try {
        playerRef.current.setVolume(volume);
        playerRef.current.playVideo();
        console.log('Manual play started successfully');
      } catch (error) {
        console.error('Error in manual play:', error);
        setTimeout(() => {
          createYouTubePlayer(nextContainer, { allowFullscreen: true, autoplay: 1 });
        }, 1000);
      }
    } else {
      console.log('Player not ready, recreating...');
      createYouTubePlayer(nextContainer, { allowFullscreen: true, autoplay: 1 });
    }
  }, [volume, createYouTubePlayer]);

  // 停止视频播放
  const stopVideo = useCallback(() => {
    console.log('Stopping video playback');
    setShowPlayPrompt(false);
    
    if (playerRef.current) {
      try {
        console.log('Stopping and destroying YouTube player');
        if (typeof playerRef.current.stopVideo === 'function') {
          playerRef.current.stopVideo();
        }
        if (typeof playerRef.current.destroy === 'function') {
          playerRef.current.destroy();
        }
        playerRef.current = null;
      } catch (error) {
        console.error('Error stopping video:', error);
      }
    }
    
    // 清空播放器容器，保持外层节点由 React 接管
    const cleanupWrapper = () => {
      try {
        if (playerMountIdRef.current) {
          const mountNode = document.getElementById(playerMountIdRef.current);
          if (mountNode?.parentElement) {
            // 安全地清理子节点
            const parent = mountNode.parentElement;
            while (parent.firstChild) {
              try {
                if (parent.firstChild && parent.contains(parent.firstChild)) {
                  parent.removeChild(parent.firstChild);
                } else {
                  break; // 如果节点不存在或不在父节点中，停止循环
                }
              } catch (error) {
                console.warn('Error removing child node from mount:', error);
                break; // 如果出错就停止循环
              }
            }
          }
          playerMountIdRef.current = null;
        } else if (playerContainerIdRef.current) {
          const wrapper = document.getElementById(playerContainerIdRef.current);
          if (wrapper instanceof HTMLElement) {
            // 安全地清理子节点
            while (wrapper.firstChild) {
              try {
                if (wrapper.firstChild && wrapper.contains(wrapper.firstChild)) {
                  wrapper.removeChild(wrapper.firstChild);
                } else {
                  break; // 如果节点不存在或不在父节点中，停止循环
                }
              } catch (error) {
                console.warn('Error removing child node from wrapper:', error);
                break; // 如果出错就停止循环
              }
            }
          }
        }
      } catch (error) {
        console.warn('Error in cleanupWrapper:', error);
      }
    };

    cleanupWrapper();

    playerContainerIdRef.current = 'youtube-player';
  }, []);

  useEffect(() => {
    alarmRingingRef.current = alarmRinging;
  }, [alarmRinging]);

  // 提取视频ID
  const extractVideoId = useCallback((url) => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/,
      /^([a-zA-Z0-9_-]{11})$/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  }, []);

  // 简化的保存逻辑
  const isInitialMount = useRef(true);
  // 保存设置的逻辑
  useEffect(() => {
    // 跳过初始挂载时的保存
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    const settings = {
      alarmHour,
      alarmMinute,
      snoozeMinutes,
      selectedMelody,
      growing
    };
    
    saveToStorage(STORAGE_KEYS.VIDEO_ALARM_SETTINGS, settings);
  }, [alarmHour, alarmMinute, snoozeMinutes, selectedMelody, growing]);

  // 保存视频特定设置
  useEffect(() => {
    // 跳过初始挂载时的保存
    if (isInitialMount.current) {
      return;
    }
    
    const videoSettings = {
      youtubeUrl,
      videoId,
      volume
    };
    
    saveToStorage('video_alarm_settings', videoSettings);
  }, [youtubeUrl, videoId, volume]);

  // 监听时间设置变化，重置激活状态（但排除 snooze 操作）
  useEffect(() => {
    // 跳过初始挂载
    if (isInitialMount.current) {
      return;
    }
    
    // 如果是 snooze 操作，跳过重置
    if (isSnoozingRef.current) {
      isSnoozingRef.current = false;
      timeSettingsRef.current = { alarmHour, alarmMinute };
      return;
    }
    
    // 检查时间设置是否发生变化
    if (timeSettingsRef.current.alarmHour !== alarmHour || timeSettingsRef.current.alarmMinute !== alarmMinute) {
      console.log('📺 [视频闹钟] 时间设置已更改，重置激活状态');
      setAlarmActiveWithSave(false);
      setAlarmRinging(false);
      
      // 停止当前音频
      if (audioControlRef.current) {
        audioControlRef.current.stop();
        audioControlRef.current = null;
      }
      stopAllAudio();
      
      // 停止视频播放
      stopVideo();
      
      // 更新引用
      timeSettingsRef.current = { alarmHour, alarmMinute };
    }
  }, [alarmHour, alarmMinute, setAlarmActiveWithSave, stopVideo]);

  const triggerAlarm = useCallback(() => {
    // 只在 video 页面时触发视频闹钟
    if (typeof window !== 'undefined' && window.location.pathname !== '/video-alarm') {
      console.log('📺 [视频闹钟] 不在 video 页面，跳过触发');
      return;
    }
    
    console.log('📺 [视频闹钟] 触发闹钟 - 只播放视频');
    setAlarmRinging(true);
    // 视频闹钟只播放视频，不播放额外的闹钟铃声
  }, []);

  // 监听 alarmRinging 状态变化
  useEffect(() => {
    if (alarmRinging && videoId) {
      console.log('Alarm is ringing, checking fullscreen status...');
      
      // 播放警告音
      try {
        playAlertSound();
      } catch (error) {
        console.error('Error playing alert sound:', error);
      }
      
      // 只在非全屏模式且在视频闹钟页面下创建YouTube播放器
      if (!isFullscreen && ytApiReady && window.location.pathname === '/video-alarm') {
        console.log('Non-fullscreen mode, starting video playback');
        setShowPlayPrompt(true);
        
        setTimeout(() => {
          createYouTubePlayer();
        }, 100);
      } else if (isFullscreen) {
        console.log('Fullscreen mode, skipping video playback - audio only');
        setShowPlayPrompt(false);
      } else {
        console.log('YouTube API not ready or not in video alarm page, waiting...');
      }
    } else if (!alarmRinging) {
      stopVideo();
    }
  }, [alarmRinging, ytApiReady, videoId, isFullscreen, playAlertSound, createYouTubePlayer, stopVideo]);

  // 当闹钟开始响铃时，自动创建并播放视频（仅在非全屏模式）
  useEffect(() => {
    if (alarmRinging && videoId && !isFullscreen) {
      console.log('Alarm is ringing in non-fullscreen mode, auto-starting video playback');
      const timer = setTimeout(() => {
        createYouTubePlayer();
      }, 300);
      
      return () => clearTimeout(timer);
    } else if (alarmRinging && videoId && isFullscreen) {
      console.log('Alarm is ringing in fullscreen mode, skipping video creation');
    }
  }, [alarmRinging, videoId, isFullscreen, createYouTubePlayer]);

  // 加载 YouTube IFrame API
  useEffect(() => {
    // 检查是否已经有YouTube API脚本
    const existingScript = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
    
    if (!window.YT && !existingScript) {
      console.log('Loading YouTube API...');
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      tag.id = 'youtube-api-script'; // 添加ID以便识别
      
      // 确保有script标签存在
      const firstScriptTag = document.getElementsByTagName('script')[0];
      if (firstScriptTag && firstScriptTag.parentNode) {
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      } else {
        // 如果没有找到script标签，直接添加到head
        document.head.appendChild(tag);
      }
      
      // 设置全局回调函数，但要检查是否已经设置过
      if (!window.onYouTubeIframeAPIReady) {
        window.onYouTubeIframeAPIReady = () => {
          console.log('YouTube API loaded successfully');
          setYtApiReady(true);
        };
      }
    } else if (window.YT && window.YT.Player) {
      console.log('YouTube API already loaded');
      setYtApiReady(true);
    } else if (existingScript) {
      console.log('YouTube API script already exists, waiting for load...');
      // 如果脚本已存在但API未准备好，设置回调
      if (!window.onYouTubeIframeAPIReady) {
        window.onYouTubeIframeAPIReady = () => {
          console.log('YouTube API loaded successfully (existing script)');
          setYtApiReady(true);
        };
      }
    }
  }, []);

  // 初始化时检查激活状态和时间
  useEffect(() => {
    if (savedActiveState.isActive && savedActiveState.activatedAt) {
      console.log('🔄 [视频闹钟] 检查激活的闹铃状态...');
      
      const now = new Date();
      const alarm = new Date();
      alarm.setHours(parseInt(alarmHour), parseInt(alarmMinute), 0, 0);
      
      // 如果闹铃时间已经过了，设置为明天
      if (alarm <= now) {
        alarm.setDate(alarm.getDate() + 1);
      }
      
      // 检查是否应该立即响铃（允许1分钟的误差）
      const timeDiff = now - alarm;
      if (timeDiff >= 0 && timeDiff <= 60000) { // 1分钟内
        console.log('📺 [视频闹钟] 闹铃时间已到，立即触发');
        triggerAlarm();
      } else {
        console.log('✅ [视频闹钟] 闹铃仍在激活状态，继续倒计时');
      }
    }
  }, [triggerAlarm, savedActiveState.isActive, savedActiveState.activatedAt, alarmHour, alarmMinute]);

  // Calculate time left until alarm
  useEffect(() => {
    if (alarmActive) {
      const now = new Date();
      const alarm = new Date();
      alarm.setHours(parseInt(alarmHour), parseInt(alarmMinute), 0, 0);
      
      if (alarm <= now) {
        alarm.setDate(alarm.getDate() + 1);
      }
      
      const diff = alarm - now;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setTimeLeft(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
      
      // Check if alarm should ring
      if (hours === 0 && minutes === 0 && seconds === 0) {
        triggerAlarm();
      }
    }
  }, [currentTime, alarmActive, alarmHour, alarmMinute, triggerAlarm]);

  const stopAlarm = () => {
    console.log('🛑 [视频闹钟] 停止闹钟');
    setAlarmRinging(false);
    setAlarmActiveWithSave(false);
    
    // 停止当前播放的音频
    if (audioControlRef.current) {
      audioControlRef.current.stop();
      audioControlRef.current = null;
    }
    
    // 确保停止所有音频
    stopAllAudio();
    
    // 停止视频播放
    stopVideo();
  };

  const snoozeAlarm = () => {
    console.log('😴 [视频闹钟] 贪睡闹钟');
    setAlarmRinging(false);
    
    // 停止当前音频
    if (audioControlRef.current) {
      audioControlRef.current.stop();
      audioControlRef.current = null;
    }
    stopAllAudio();
    
    // 停止视频播放
    stopVideo();
    
    // 计算新的闹钟时间
    const newMinute = (parseInt(alarmMinute) + parseInt(snoozeMinutes)) % 60;
    const newHour = (parseInt(alarmHour) + Math.floor((parseInt(alarmMinute) + parseInt(snoozeMinutes)) / 60)) % 24;
    const newMinuteStr = newMinute.toString().padStart(2, '0');
    const newHourStr = newHour.toString().padStart(2, '0');
    
    // 设置 snooze 标志，防止时间变化监听器重置激活状态
    isSnoozingRef.current = true;
    
    // 更新时间
    setAlarmMinute(newMinuteStr);
    setAlarmHour(newHourStr);
    
    // 确保闹钟保持激活状态并保存
    if (!alarmActive) {
      setAlarmActiveWithSave(true);
    } else {
      // 即使已经是激活状态，也重新保存以确保状态同步
      saveActiveState(true);
    }
    
    // 更新时间引用
    timeSettingsRef.current = { alarmHour: newHourStr, alarmMinute: newMinuteStr };
  };

  const testSound = () => {
    console.log(`🔊 [视频闹钟] 测试音效: ${selectedMelody}`);
    // 使用新的测试音效函数，播放当前选中的音效
    playTestSound(selectedMelody, 2000).then(audioControl => {
      console.log(`正在测试音效: ${selectedMelody}`);
    }).catch(error => {
      console.error('[视频闹钟] 测试音效播放失败:', error);
    });
  };

  return {
    alarmHour,
    alarmMinute,
    snoozeMinutes,
    selectedMelody,
    growing,
    alarmActive,
    alarmRinging,
    timeLeft,
    setAlarmHour,
    setAlarmMinute,
    setSnoozeMinutes,
    setSelectedMelody,
    setGrowing,
    setAlarmActive: setAlarmActiveWithSave,
    stopAlarm,
    snoozeAlarm,
    testSound,
    // 视频相关
    youtubeUrl,
    setYoutubeUrl,
    videoId,
    setVideoId,
    volume,
    setVolume,
    ytApiReady,
    showPlayPrompt,
    audioUnlocked,
    popularVideos,
    unlockAudio,
    playAlertSound,
    createYouTubePlayer,
    manualStartPlay,
    stopVideo,
    extractVideoId
  };
}
